import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Card, Button, Offcanvas } from 'react-bootstrap';
import { API } from '../../../../api/URL_API';
import { Link } from 'react-router-dom';
import { ArrowUp, ChevronRight, ArrowRight, List } from 'react-bootstrap-icons';
import { CardMedia, CardContent } from '@mui/material';
import '../../styles/CategoriasUni.css';
import { createSlug } from '../../../../utils/slug';
import ModalAuto from '../../componentes/ModalAuto';

function CategoriasUni({ idSucursal, soloSeccion = null }) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [seccionActual, setSeccionActual] = useState(null);
    const hasRestoredScrollRef = useRef(false);
    const esVistaSeccion = seccionActual !== null;

    // ✅ Función para determinar el estado inicial del acordeón
    const getInitialExpandedState = () => {
        const lastInteraction = localStorage.getItem("lastCategoryInteraction");
        const expandedSection = localStorage.getItem("expandedSection");

        // Si no hay interacción previa, activar la primera sección por defecto
        if (!lastInteraction) {
            return 'panel0'; // Primera sección expandida por defecto
        }

        const timeSinceLastInteraction = Date.now() - parseInt(lastInteraction, 10);
        const thirtyMinutes = 5 * 60 * 1000; // 30 minutos en milisegundos

        // Si han pasado más de 30 minutos, cerrar el acordeón y limpiar datos
        if (timeSinceLastInteraction > thirtyMinutes) {
            localStorage.removeItem("expandedSection");
            localStorage.removeItem("scrollPosition");
            localStorage.removeItem("lastCategoryInteraction");
            return 'panel0'; // Mostrar la primera sección después de inactividad
        }

        // Si la interacción fue reciente, mantener la sección expandida
        return expandedSection || 'panel0'; // Por defecto, primera sección
    };

    const [expanded, setExpanded] = useState(getInitialExpandedState());

    // ✅ Escuchar el scroll para mostrar/ocultar el botón flotante
    useEffect(() => {
        const originalScrollRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';

        const handleScroll = () => {
            const currentScrollY = Math.round(window.scrollY);
            localStorage.setItem('scrollPosition', currentScrollY.toString());

            // Mostrar el botón si se ha hecho scroll más de 300px
            if (currentScrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.history.scrollRestoration = originalScrollRestoration || 'auto';
        };
    }, []);

    // ✅ Función para subir hasta lo más alto
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // ✅ Restaurar el scroll después de que se cargue el contenido
    useEffect(() => {
        hasRestoredScrollRef.current = false;

        const cargarCategorias = async () => {
            try {
                // Vista normal
                if (!soloSeccion) {
                    const response = await fetch(
                        `${API}/api/mostrarCategorias/categorias`
                    );

                    if (!response.ok) {
                        throw new Error("Error al cargar categorías");
                    }

                    const categoriasData = await response.json();
                    setData(categoriasData);
                }

                // Vista de una sección
                else {
                    // 1️⃣ Resolver slug
                    const slugResponse = await fetch(
                        `${API}/api/mostrarCategorias/seccion/slug/${soloSeccion}`
                    );

                    if (!slugResponse.ok) {
                        throw new Error("Sección no encontrada");
                    }

                    const seccion = await slugResponse.json();

                    // 2️⃣ Obtener categorías de esa sección
                    const categoriasResponse = await fetch(
                        `${API}/api/mostrarCategorias/categorias/seccion/${seccion.idSeccion}`
                    );

                    if (!categoriasResponse.ok) {
                        throw new Error("Error al cargar categorías");
                    }

                    const categorias = await categoriasResponse.json();
                    setSeccionActual(categorias);
                }

                setIsLoading(false);
            }
            catch (error) {
                console.error(error);
                setIsLoading(false);
            }
        };

        cargarCategorias();
    }, [soloSeccion]);

    useEffect(() => {
        if (isLoading || hasRestoredScrollRef.current) return;

        const restoreScrollPosition = () => {
            const savedScrollPosition = Number(localStorage.getItem('scrollPosition') || '0');

            if (savedScrollPosition > 0) {
                window.scrollTo({
                    top: savedScrollPosition,
                    behavior: 'auto'
                });
            }

            hasRestoredScrollRef.current = true;
        };

        const timeoutId = window.setTimeout(() => {
            window.requestAnimationFrame(restoreScrollPosition);
        }, 150);

        return () => window.clearTimeout(timeoutId);
    }, [isLoading, soloSeccion, data.length, seccionActual]);

    // ✅ Manejar la expansión/colapso del acordeón
    const handleAccordionChange = (panelIndex) => {
        const currentTime = Date.now();

        if (expanded === panelIndex) {
            setExpanded(false);
            localStorage.removeItem("expandedSection");
            localStorage.removeItem("scrollPosition");
            localStorage.removeItem("lastCategoryInteraction");
        } else {
            localStorage.setItem("scrollPosition", window.scrollY);
            localStorage.setItem("expandedSection", panelIndex);
            localStorage.setItem("lastCategoryInteraction", currentTime.toString());
            setExpanded(panelIndex);

            // 👇 UX improvement
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const seccionesMostrar = soloSeccion
        ? (seccionActual ? [seccionActual] : [])
        : data;

    // ✅ Guardar la posición del scroll antes de navegar a una categoría
    const handleCategoryClick = (panelIndex) => {
        const currentTime = Date.now();
        localStorage.setItem("scrollPosition", window.scrollY);
        localStorage.setItem("expandedSection", panelIndex);
        localStorage.setItem("lastCategoryInteraction", currentTime.toString());
    };

    // ✅ Componente Skeleton para la carga
    const SkeletonCard = () => (
        <div
            className="position-relative border-0 h-100 shadow-sm"
            style={{
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0'
            }}
        >
            <div
                style={{
                    width: '100%',
                    aspectRatio: '1',
                    backgroundColor: '#e9ecef',
                    animation: 'pulse 2s infinite'
                }}
            />
        </div>
    );

    return (
        <div className="p-3 p-md-5 color-bg-secundary">
            <ModalAuto /> {/* Modal de tutorial en el apartado de categorias de venta de unidad */}
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            <Container fluid>
                <Row>
                    {/* 🧭 SIDEBAR DESKTOP */}
                    {!esVistaSeccion && (
                        <Col md={3} className="d-none d-md-block pe-4">
                            {isLoading ? (
                                <div
                                    style={{
                                        width: '120px',
                                        height: '28px',
                                        backgroundColor: '#e9ecef',
                                        animation: 'pulse 2s infinite',
                                        borderRadius: '6px',
                                        marginBottom: '1rem'
                                    }}
                                />
                            ) : (
                                <h4 className="color-primary fw-bold fs-5 text-uppercase mb-3">
                                    Secciones
                                </h4>
                            )}
                            <div>
                                {isLoading ? (
                                    [...Array(6)].map((_, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                height: '45px',
                                                backgroundColor: '#e9ecef',
                                                animation: 'pulse 2s infinite',
                                                borderRadius: '6px',
                                                marginBottom: '8px'
                                            }}
                                        />
                                    ))
                                ) : (
                                    data.map((seccion, index) => {
                                        const isActive = expanded === `panel${index}`;

                                        return (
                                            <div
                                                key={index}
                                                onClick={() => handleAccordionChange(`panel${index}`)}
                                                className='d-flex align-items-center justify-content-between px-3 py-2 mb-2'
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isActive ? '#2E0205' : 'transparent',
                                                    color: isActive ? '#fff' : '#2E0205',
                                                    borderRadius: '6px',
                                                    fontWeight: isActive ? '600' : '500',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.backgroundColor = 'rgba(46, 2, 5, 0.1)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <span className='d-flex align-items-center gap-2'>
                                                    <span>{seccion.nomSeccion}</span>
                                                </span>
                                                <ChevronRight size={18} />
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </Col>
                    )}
                    {/* 📱 BOTÓN SECCIONES MOBILE */}
                    {!esVistaSeccion && (
                        <Col xs={12} className="d-md-none mb-3">
                            <div className="d-flex align-items-center justify-content-between">
                                <Button
                                    variant="outline-dark"
                                    size="sm"
                                    onClick={() => setShowOffcanvas(true)}
                                    className="d-flex align-items-center gap-2"
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    <List size={18} />
                                    Secciones
                                </Button>
                                <h5 className='color-primary fw-bold mb-0' style={{ fontSize: '0.95rem' }}>
                                    {isLoading ? 'Cargando...' : (expanded && data[parseInt(expanded.replace('panel', ''))]?.nomSeccion) || 'Secciones'}
                                </h5>
                            </div>
                        </Col>
                    )}

                    {esVistaSeccion && seccionActual && (
                        <div className="mb-4 text-center">
                            <h2 className='fw-bold text-uppercase mb-3' style={{ fontSize: '1.75rem', letterSpacing: '0.5px', color: '#2E0205' }}>
                                {seccionActual.nomSeccion}
                            </h2>
                            <hr
                                style={{
                                    width: "120px",
                                    margin: "15px auto",
                                    borderTop: "3px solid #2E0205",
                                    opacity: 1
                                }}
                            />
                        </div>
                    )}

                    {/* OFFCANVAS SECCIONES */}
                    <Offcanvas
                        show={showOffcanvas}
                        onHide={() => setShowOffcanvas(false)}
                        placement="start"
                        className="color-bg-secundary"
                    >
                        <Offcanvas.Header closeButton>
                            <Offcanvas.Title className="color-primary fw-bold">
                                Secciones
                            </Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body className="p-0">
                            {isLoading ? (
                                <div className="p-3">
                                    {[...Array(6)].map((_, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                height: '45px',
                                                backgroundColor: '#e9ecef',
                                                animation: 'pulse 2s infinite',
                                                borderRadius: '6px',
                                                marginBottom: '8px'
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div>
                                    {data.map((seccion, index) => {
                                        const isActive = expanded === `panel${index}`;
                                        return (
                                            <div
                                                key={index}
                                                onClick={() => {
                                                    handleAccordionChange(`panel${index}`);
                                                    setShowOffcanvas(false);
                                                }}
                                                className="px-3 py-3 border-bottom"
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isActive ? 'rgba(46, 2, 5, 0.1)' : 'transparent',
                                                    borderLeft: isActive ? '4px solid #2E0205' : '4px solid transparent',
                                                    fontWeight: isActive ? '600' : '500',
                                                    color: isActive ? '#2E0205' : '#333',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span className='d-flex align-items-center gap-2'>
                                                        <span>{seccion.nomSeccion}</span>
                                                    </span>
                                                    {isActive && <ChevronRight size={18} className="color-primary" />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Offcanvas.Body>
                    </Offcanvas>

                    {/* 🧱 CATEGORÍAS */}
                    <Col xs={12} md={esVistaSeccion ? 12 : 9}>
                        {isLoading ? (
                            <Row className="g-4">
                                {[...Array(8)].map((_, index) => (
                                    <Col key={index} xs={6} sm={4} md={3}>
                                        <SkeletonCard />
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            seccionesMostrar.map((seccion, index) => {
                                if (!esVistaSeccion && expanded !== `panel${index}`) {
                                    return null;
                                }

                                return (
                                    <Row key={index} className="g-4">
                                        {seccion.categorias.map((categoria) => {
                                            return (
                                                <Col key={categoria.idCategoria} xs={6} sm={4} md={3}>
                                                    <Link
                                                        to={`/${idSucursal}/Productos/${createSlug(categoria.nomCategoria)}`}
                                                        className="text-decoration-none d-flex flex-column h-100"
                                                        onClick={() => handleCategoryClick(`panel${index}`)}
                                                    >
                                                        <Card
                                                            className="h-100 w-100"
                                                            style={{
                                                                boxShadow: '0 2px 8px rgba(43, 35, 94, 0.15)',
                                                                borderRadius: '12px',
                                                                overflow: 'hidden',
                                                                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                                                cursor: 'pointer',
                                                                backgroundColor: '#f5efe8'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(43, 35, 94, 0.25)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(43, 35, 94, 0.15)';
                                                            }}
                                                        >
                                                            <div
                                                                className='position-relative'
                                                                style={{
                                                                    width: '100%',
                                                                    aspectRatio: '1 / 1',
                                                                    overflow: 'hidden',
                                                                    backgroundColor: '#f0f0f0'
                                                                }}
                                                            >
                                                                <CardMedia
                                                                    component='img'
                                                                    image={categoria.imgCategoria}
                                                                    alt={`ver ${categoria.nomCategoria}`}
                                                                    title={`crisha-${categoria.nomCategoria}`}
                                                                    className='card-media-image'
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            </div>
                                                            <CardContent className='p-3 d-flex flex-column justify-content-between flex-grow-1'>
                                                                <div className='d-flex justify-content-between align-items-flex-start gap-2'>
                                                                    <div>
                                                                        <h5 className='fw-bold mb-2' style={{ fontSize: '1rem', lineHeight: '1.3', color: '#2E0205' }}>
                                                                            {categoria.nomCategoria}
                                                                        </h5>
                                                                        <p className='mb-0 text-muted' style={{ fontSize: '0.85rem' }}>
                                                                            {categoria.numProductos || '0'} productos
                                                                        </p>
                                                                    </div>
                                                                    <ArrowRight size={20} className='color-primary flex-shrink-0' />
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    </Link>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                );
                            })
                        )}
                    </Col>

                </Row>
            </Container>

            {/* ✅ Botón Flotante Scroll to Top */}
            {showScrollTop && (
                <Button
                    onClick={scrollToTop}
                    className="position-fixed"
                    style={{
                        bottom: '30px',
                        right: '20px',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        backgroundColor: '#2E0205',
                        borderColor: '#2E0205',
                        boxShadow: '0 4px 12px rgba(255, 255, 255, 0.4)',
                        animation: 'slideUp 0.3s ease-out',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#2E0205';
                        e.currentTarget.style.borderColor = '#2E0205';
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(240, 240, 240, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#2E0205';
                        e.currentTarget.style.borderColor = '#2E0205';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.4)';
                    }}
                    title="Volver al inicio"
                >
                    <ArrowUp size={24} className="text-white" />
                </Button>
            )}
        </div>
    );
}

export default CategoriasUni;
